import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { collection, doc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { UploadResult } from 'firebase/storage';

import { useNavigate } from 'react-router-dom';

import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Switch from '@mui/material/Switch';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import OrgDefaultsEditor from '../components/org/OrgDefaultsEditor';
import OrgGeneralSettingsEditor from '../components/org/OrgGeneralSettingsEditor';
import OrgReviewAndFinalize from '../components/org/OrgReviewAndFinalize';
import { ActionProps, useApp } from '../contextProviders/AppProvider';
import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { useOrgMutators } from '../dataMutators/useOrgMutators';
import { firestore, storage } from '../firebase';
import DefaultObjects from '../types/DefaultObjects';
import Org from '../types/Org';

type Step = 'general' | 'defaults' | 'review';

export default function OrgCreatePage(): ReactElement {
  const { setPageName } = useApp();
  const { isGod } = useAuth();
  const { userState } = useData();
  const { addOrg } = useOrgMutators();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('general');
  const [isEditing, setIsEditing] = useState(false);
  const [isAddedToUserProfile, setIsAddedToUserProfile] = useState(true);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pageActions, setPageActions] = useState<ActionProps[] | null>(null);

  const org = useMemo((): Org => {
    const orgRef = doc(collection(firestore, 'org'));
    return {
      ...DefaultObjects.Org,
      itemId: orgRef.id,
      creatorId: userState.userProfile?.itemId || '',
    };
  }, [userState.userProfile?.itemId]);

  const [currentOrg, setCurrentOrg] = useState<Org>(org);

  // Update currentOrg when org changes (if user profile loads later)
  useEffect(() => {
    setCurrentOrg((prev) => ({
      ...prev,
      creatorId: org.creatorId,
    }));
  }, [org.creatorId]);

  useEffect(() => {
    const pageTitles: Record<Step, string> = {
      general: 'Create Org',
      defaults: 'Set defaults',
      review: 'Review and create',
    };
    setPageName(pageTitles[step]);
  }, [setPageName, step]);

  // Handle browser back button - go back to previous step instead of leaving page
  useEffect(() => {
    if (step === 'general') return;

    // Push a history entry when entering non-general steps
    window.history.pushState({ step }, '');

    const handlePopState = () => {
      setStep((currentStep) => {
        if (currentStep === 'review') return 'defaults';
        if (currentStep === 'defaults') return 'general';
        return currentStep;
      });
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step]);

  const handleNext = useCallback(() => {
    if (step === 'general') {
      setStep('defaults');
    } else if (step === 'defaults') {
      setStep('review');
    }
  }, [step]);

  const handleCreate = useCallback(async () => {
    try {
      let orgToSave = currentOrg;

      // If there's a pending logo file, upload it now
      if (pendingLogoFile) {
        const logoRef = ref(storage, `org/${orgToSave.itemId}/publicFile/${orgToSave.itemId}-logo`);
        const snapshot = await uploadBytes(logoRef, pendingLogoFile);
        orgToSave = { ...orgToSave, logo: snapshot.ref.name };
      }

      const addedOrg = await addOrg(orgToSave, true, true, isAddedToUserProfile);
      if (addedOrg) {
        navigate(`/org/${addedOrg.itemId}`, { replace: true });
      } else {
        console.error('Org not added - addOrg returned:', addedOrg);
      }
    } catch (error) {
      console.error('Error saving org:', error);
    }
  }, [currentOrg, pendingLogoFile, addOrg, isAddedToUserProfile, navigate]);

  const handleOrgChange = useCallback((updatedOrg: Org) => {
    setCurrentOrg(updatedOrg);
  }, []);

  const handleUpdate = useCallback(<K extends keyof Org>(field: K, value: Org[K]) => {
    setCurrentOrg((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleImageUpdated = useCallback((snapshot: UploadResult | null) => {
    if (snapshot) {
      setCurrentOrg((prev) => ({ ...prev, logo: snapshot.ref.name }));
    }
  }, []);

  const handleFileSelected = useCallback((file: File | null) => {
    setPendingLogoFile(file);
  }, []);

  const validateUser = (): boolean => {
    return isGod;
  };

  // Store callbacks in refs to avoid infinite loops in useEffect
  const handleNextRef = useRef(handleNext);
  const handleCreateRef = useRef(handleCreate);

  useEffect(() => {
    handleNextRef.current = handleNext;
    handleCreateRef.current = handleCreate;
  });

  // Build page actions based on step
  useEffect(() => {
    switch (step) {
      case 'general':
        setPageActions([{
          name: 'Next',
          icon: ArrowForwardIcon,
          handleClick: () => handleNextRef.current(),
          variant: 'contained',
          color: 'primary',
          disabled: !currentOrg.name.trim() || !currentOrg.shortName.trim(),
        }]);
        break;
      case 'defaults':
        setPageActions([{
          name: 'Review',
          icon: ArrowForwardIcon,
          handleClick: () => handleNextRef.current(),
          variant: 'contained',
          color: 'primary',
        }]);
        break;
      case 'review':
        setPageActions([{
          name: 'Create',
          icon: CheckIcon,
          handleClick: () => handleCreateRef.current(),
          variant: 'contained',
          color: 'primary',
        }]);
        break;
    }
  }, [step, currentOrg.name, currentOrg.shortName]);

  // God actions for "Add to user profile?" toggle
  const godActions = isGod ? (
    <FormGroup>
      <FormControlLabel
        id="isAddedToUserProfile"
        value="top"
        aria-label="God can create orgs and not be a part of them. By default, they are added to the user profile."
        aria-required="false"
        label="Add to User Profile?"
        control={
          <Switch
            id="isAddedToUserProfileSwitch"
            checked={isAddedToUserProfile}
            color="secondary"
            onChange={(e) => setIsAddedToUserProfile(e.target.checked)}
          />
        }
      />
    </FormGroup>
  ) : null;

  return (
    <GratiPageWrapper
      validateUser={validateUser}
      pageActions={pageActions}
      godActions={godActions}
    >
      {step === 'general' && (
        <OrgGeneralSettingsEditor
          org={currentOrg}
          isNew={true}
          onUpdate={handleUpdate}
          onFileSelected={handleFileSelected}
        />
      )}
      {step === 'defaults' && (
        <OrgDefaultsEditor
          org={currentOrg}
          onUpdate={handleUpdate}
        />
      )}
      {step === 'review' && (
        <OrgReviewAndFinalize
          org={currentOrg}
          isNew={true}
          isEditing={isEditing}
          onOrgChange={handleOrgChange}
          onEditingClose={() => setIsEditing(false)}
          onEditingOpen={() => setIsEditing(true)}
          onImageUpdated={handleImageUpdated}
          onFileSelected={handleFileSelected}
        />
      )}
    </GratiPageWrapper>
  );
}
